/**
 * Servicio admin para colección raíz `productos`.
 * Status: 'activa' | 'pausada' | 'eliminada'. Si no existe, se trata como 'activa' en UI.
 */

import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { enriquecerConNombreEmpresa } from '../../empresa/services/empresaNombreHelper';

const COL = 'productos';
const DEFAULT_LIMIT = 100;

/** Mapper: nombre para listado (variantes en docs existentes) */
export function mapProductoName(docData) {
  return docData?.nombre ?? docData?.title ?? docData?.nombreProducto ?? '(Sin nombre)';
}

/** Mapper: empresa para listado */
export function mapProductoEmpresa(docData) {
  return docData?.empresaNombre ?? docData?.empresaId ?? '—';
}

/** Normaliza status: ausente o legacy => 'activa' */
export function normalizeStatus(docData) {
  const s = docData?.status ?? docData?.estado;
  if (s === 'pausada' || s === 'eliminada') return s;
  return 'activa';
}

/**
 * Lista productos. Filtro por status opcional; búsqueda se hace en cliente sobre resultados.
 * @param {{ statusFilter?: 'activa'|'pausada'|'eliminada'|'activa_pausada'|'todas', limit?: number }} opts
 */
export async function fetchProductos(opts = {}) {
  const { statusFilter = 'activa_pausada', limit: limitNum = DEFAULT_LIMIT } = opts;
  const col = collection(db, COL);

  let list = [];
  try {
    const q = query(col, orderBy('fechaCreacion', 'desc'), limit(limitNum));
    const snap = await getDocs(q);
    list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    try {
      const q = query(col, orderBy('updatedAt', 'desc'), limit(limitNum));
      const snap = await getDocs(q);
      list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e2) {
      const snap = await getDocs(col);
      list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const ta = a.fechaCreacion?.toMillis?.() ?? a.updatedAt?.toMillis?.() ?? 0;
        const tb = b.fechaCreacion?.toMillis?.() ?? b.updatedAt?.toMillis?.() ?? 0;
        return tb - ta;
      });
      list = list.slice(0, limitNum);
    }
  }

  list = list.map((item) => ({
    ...item,
    _displayName: mapProductoName(item),
    _displayEmpresa: mapProductoEmpresa(item),
    _status: normalizeStatus(item),
  }));

  await enriquecerConNombreEmpresa(list);

  if (statusFilter === 'activa') list = list.filter((p) => p._status === 'activa');
  else if (statusFilter === 'pausada') list = list.filter((p) => p._status === 'pausada');
  else if (statusFilter === 'eliminada') list = list.filter((p) => p._status === 'eliminada');
  else if (statusFilter === 'activa_pausada') list = list.filter((p) => p._status === 'activa' || p._status === 'pausada');

  return list;
}

/**
 * Actualiza un producto (patch). Setea updatedAt y opcionalmente nombreLower.
 */
export async function updateProducto(id, patch) {
  const ref = doc(db, COL, id);
  const data = { ...patch, updatedAt: serverTimestamp() };
  if (patch.nombre != null) data.nombreLower = String(patch.nombre).toLowerCase().trim();
  await updateDoc(ref, data);
}

/**
 * Pausar: status = 'pausada', pausedAt, pausedBy.
 * Reanudar: status = 'activa', resumedAt, resumedBy.
 */
export async function toggleProductoPause(id, currentStatus, userId) {
  const ref = doc(db, COL, id);
  if (currentStatus === 'pausada') {
    await updateDoc(ref, {
      status: 'activa',
      resumedAt: serverTimestamp(),
      resumedBy: userId ?? null,
      updatedAt: serverTimestamp(),
    });
  } else {
    await updateDoc(ref, {
      status: 'pausada',
      pausedAt: serverTimestamp(),
      pausedBy: userId ?? null,
      updatedAt: serverTimestamp(),
    });
  }
}

/** Soft delete: status = 'eliminada', deletedAt, deletedBy */
export async function softDeleteProducto(id, userId) {
  const ref = doc(db, COL, id);
  await updateDoc(ref, {
    status: 'eliminada',
    deletedAt: serverTimestamp(),
    deletedBy: userId ?? null,
    updatedAt: serverTimestamp(),
  });
}
